import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import axios from 'axios';
import FundingTermsModal from '../components/FundingTermsModal';
import FundDealModal from '../components/FundDealModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const SubmittedDeals = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [showFundDealModal, setShowFundDealModal] = useState(false);

  useEffect(() => {
    loadDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDeals = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/deals?type=submitted`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeals(response.data || []);
    } catch (error) {
      console.error('Error loading deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDeal = async (deal) => {
    if (!window.confirm(`Delete deal for ${deal.business}? This action cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/deals/${deal.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: 'Success', description: `Deal for ${deal.business} deleted successfully` });
      loadDeals();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete deal', variant: 'destructive' });
    }
  };

  const handleKillDeal = async (deal) => {
    if (!window.confirm(`Kill deal for ${deal.business}? Status will be updated to Killed.`)) return;
    
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/deals/${deal.id}/kill`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: 'Success', description: `Deal killed` });
      loadDeals();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to kill deal', variant: 'destructive' });
    }
  };

  const handleDeclineDeal = async (deal) => {
    if (!window.confirm(`Decline deal for ${deal.business}? Status will be updated to Declined.`)) return;
    
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/deals/${deal.id}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: 'Success', description: `Deal declined` });
      loadDeals();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to decline deal', variant: 'destructive' });
    }
  };

  const handleApproveDeal = async (deal) => {
    if (!window.confirm(`Approve deal for ${deal.business} and move to Underwriting?`)) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/deals/${deal.id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Approved', description: `${deal.business} moved to Underwriting` });
      loadDeals();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to approve deal', variant: 'destructive' });
    }
  };

  const handleFundDeal = async (fundingData) => {
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/deals/${selectedDeal.id}/fund`, fundingData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: 'Success', description: `Deal funded successfully!` });
      setShowFundDealModal(false);
      setSelectedDeal(null);
      loadDeals();
      navigate('/funded-deals');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to fund deal', variant: 'destructive' });
    }
  };

  const filteredDeals = deals.filter(d => 
    d.business?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate totals
  const totals = filteredDeals.reduce((acc, deal) => ({
    advanced: acc.advanced + (deal.advanced || 0),
    payback: acc.payback + (deal.payback || 0),
    weekly: acc.weekly + (deal.weekly || 0),
    daily: acc.daily + (deal.daily || 0),
    net: acc.net + (deal.net || 0),
    collected: acc.collected + (deal.collected || 0),
    remaining: acc.remaining + (deal.remaining || 0)
  }), { advanced: 0, payback: 0, weekly: 0, daily: 0, net: 0, collected: 0, remaining: 0 });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading deals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Submitted Deals</h1>
        <div className="flex gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Search the deal"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Button variant="outline">Export to Excel</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select className="border border-gray-300 rounded px-3 py-2 text-sm">
          <option>Filter</option>
        </select>
        <select className="border border-gray-300 rounded px-3 py-2 text-sm">
          <option>Rep</option>
        </select>
        <select className="border border-gray-300 rounded px-3 py-2 text-sm">
          <option>Status</option>
        </select>
        <div className="flex gap-2 items-center">
          <span className="text-sm text-gray-600">Advanced Range:</span>
          <Input placeholder="From" className="w-32" />
          <Input placeholder="To" className="w-32" />
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left font-semibold text-gray-700">#</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Business</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Advanced $</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Payback $</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Weekly $</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Daily $</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Term / F $</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Net $</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Collected $</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Paid Off %</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Remaining $</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Rep $</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">ISO</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Inhouse company</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDeals.map((deal, idx) => (
              <tr 
                key={idx} 
                className="border-b border-gray-100 hover:bg-orange-50 transition"
              >
                <td className="px-4 py-3 text-gray-900">{deal.display_number ?? deal.id}</td>
                <td className="px-4 py-3 text-gray-600">{deal.date}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${
                    deal.status === 'New' ? 'bg-blue-100 text-blue-700' :
                    deal.status === 'Declined' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {deal.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-900 font-medium cursor-pointer hover:text-orange-600" onClick={() => navigate(`/deals/${deal.display_number ?? deal.id}`)}>{deal.business}</td>
                <td className="px-4 py-3 text-gray-900">${deal.advanced?.toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-900">${deal.payback?.toLocaleString()}</td>
                <td className="px-4 py-3 text-blue-600">${deal.weekly?.toLocaleString()}</td>
                <td className="px-4 py-3 text-blue-600">${deal.daily?.toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-600">{deal.term}</td>
                <td className="px-4 py-3 text-gray-900">${deal.net?.toLocaleString()}</td>
                <td className="px-4 py-3 text-green-600">${deal.collected?.toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-600">{deal.paid_off}%</td>
                <td className="px-4 py-3 text-gray-900">${deal.remaining?.toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-600">{deal.rep}</td>
                <td className="px-4 py-3 text-gray-600">{deal.iso}</td>
                <td className="px-4 py-3 text-gray-600">{deal.inhouse}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDeal(deal);
                        setShowFundDealModal(true);
                      }}
                      data-testid={`fund-deal-row-${deal.id}`}
                    >
                      Fund Deal
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs px-2 py-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleKillDeal(deal);
                      }}
                    >
                      Kill
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-red-600 hover:bg-red-700 text-white text-xs px-2 py-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeclineDeal(deal);
                      }}
                    >
                      Decline
                    </Button>
                    <button
                      className="px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDeal(deal);
                      }}
                      title="Delete Deal"
                      data-testid={`delete-deal-${deal.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {/* TOTALS ROW */}
            <tr className="bg-green-50 font-semibold">
              <td colSpan="4" className="px-4 py-3 text-gray-700">{filteredDeals.length} Records</td>
              <td className="px-4 py-3 text-gray-900">Total advanced<br/>${totals.advanced.toLocaleString()}</td>
              <td className="px-4 py-3 text-gray-900">Total payback<br/>${totals.payback.toLocaleString()}</td>
              <td className="px-4 py-3 text-blue-600">Total weekly<br/>${totals.weekly.toLocaleString()}</td>
              <td className="px-4 py-3 text-blue-600">Total daily<br/>${totals.daily.toLocaleString()}</td>
              <td className="px-4 py-3"></td>
              <td className="px-4 py-3 text-gray-900">Total NET<br/>${totals.net.toLocaleString()}</td>
              <td className="px-4 py-3 text-green-600">Total collected<br/>${totals.collected.toLocaleString()}</td>
              <td className="px-4 py-3"></td>
              <td className="px-4 py-3 text-gray-900">Total Remaining<br/>${totals.remaining.toLocaleString()}</td>
              <td colSpan="4"></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Funding Modal */}
      {showFundingModal && selectedDeal && (
        <FundingTermsModal
          deal={selectedDeal}
          onClose={() => {
            setShowFundingModal(false);
            setSelectedDeal(null);
          }}
          onSuccess={() => {
            setShowFundingModal(false);
            setSelectedDeal(null);
            loadDeals();
          }}
        />
      )}

      {showFundDealModal && selectedDeal && (
        <FundDealModal
          isOpen={showFundDealModal}
          deal={selectedDeal}
          onClose={() => {
            setShowFundDealModal(false);
            setSelectedDeal(null);
          }}
          onFund={handleFundDeal}
        />
      )}
    </div>
  );
};

export default SubmittedDeals;